/** @jsxImportSource @emotion/react */
import { Form as FinalForm } from 'react-final-form'

import { Button, Typography } from '@mui/material'
import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { PlatformSlug } from 'constants/consts'
import { SelectField } from 'components/Select'
import { validateRequired, validateRequiredNumber } from 'utils/validation'
import { AchievementTypeEnum } from '__generatedTypes__/globalTypes'
import { InputField } from 'components/Input'
import { PriceInput } from 'components/Input/PriceInput'
import { AwardType, LEVEL_OPTIONS, getAvailableAchievementTypes, getAwardTypeOptions, LEVEL_IMAGE_MAP } from './consts'
import * as classes from './styles'
import { AchievementsRewardsImagePreview } from './AchievementsRewardsImagePreview'

export const READ_ONLY_FOR_PLATFORMS = [
  PlatformSlug.CARSBooster,
  PlatformSlug.MCATBooster,
  PlatformSlug.GREBooster,
  PlatformSlug.NBEOPartOne,
]

export type FormValues = {
  type: {
    id: AchievementTypeEnum
    title: string
  }
  amountRequired: number
  awardType: AwardType
  description: string
  level: {
    id: string
    title: string
  }
}

type Props = {
  initialValues?: FormValues
  onSubmit: (values: FormValues) => Promise<void>
  platform: PlatformSlug
}

export const Form: FC<Props> = ({ initialValues, onSubmit, platform }) => {
  const navigate = useNavigate()
  const isReadOnly = READ_ONLY_FOR_PLATFORMS.includes(platform)

  return (
    <FinalForm<FormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ values, submitting, handleSubmit }) => (
        <form css={classes.form} onSubmit={handleSubmit}>
          <SelectField
            name="type"
            validate={validateRequired}
            items={getAvailableAchievementTypes(platform)}
            label="Achievement Type"
            placeholder="Choose Achievement Type"
            disabled={isReadOnly}
            fullWidth
          />

          <PriceInput
            name="amountRequired"
            validate={validateRequiredNumber}
            label="Amount Required"
            placeholder="Enter Amount Required"
            disabled={isReadOnly}
            fullWidth
          />

          <SelectField
            name="awardType"
            validate={validateRequired}
            items={getAwardTypeOptions(platform)}
            label="Award"
            placeholder="Choose Award"
            disabled={isReadOnly}
            fullWidth
          />

          <InputField
            name="description"
            validate={validateRequired}
            label="Description"
            placeholder="Enter Description"
            disabled={isReadOnly}
            fullWidth
          />

          <div css={classes.levelWrapper}>
            <SelectField
              name="level"
              css={classes.levelSelect}
              validate={validateRequired}
              items={LEVEL_OPTIONS}
              label="Badge Level"
              placeholder="Choose Level"
              disabled={isReadOnly}
              fullWidth
            />
            {values.level?.id && values.type?.id && Object.keys(LEVEL_IMAGE_MAP[values.type?.id])?.length && (
              <div css={classes.levelPreview}>
                <Typography variant="body2" color="gray" css={classes.levelPreviewTitle}>
                  Preview
                </Typography>

                <div css={classes.imagePreviewWrapper}>
                  <AchievementsRewardsImagePreview
                    tab="achievements"
                    level={values.level?.id}
                    achievementType={values.type?.id}
                  />
                </div>
              </div>
            )}
          </div>

          <div css={classes.formActions}>
            <Button
              variant="outlined"
              color="red"
              size="large"
              onClick={() => navigate(-1)}
              disabled={submitting}
              fullWidth
            >
              Cancel
            </Button>
            <Button color="blue" type="submit" size="large" disabled={isReadOnly || submitting} fullWidth>
              Save Edits
            </Button>
          </div>
        </form>
      )}
    />
  )
}
